/**
 * Non-text members that a Portable Text array can hold alongside text blocks.
 *
 * Portable Text treats editorial media, columns, and accordions as sibling
 * blocks rather than inline content, so a rich text field that opts into them
 * can legitimately contain zero text blocks — a video-only field is still
 * valid Portable Text.
 *
 * `createPortableTextMembers` in `@homeinstead/sanity` builds its media,
 * column, and accordion members from these constants, so the editor's insert
 * menu and this detector cannot drift apart.
 *
 * @maintenance Adding a new non-text member type to a rich text schema
 * requires adding it here too, or placeholder resolution will silently drop
 * fields that contain only that member.
 */
const PORTABLE_TEXT_MEDIA_MEMBER_TYPES = [
  "mediaImage",
  "mediaVideo",
  "mediaAudio",
] as const;

const PORTABLE_TEXT_COLUMNS_MEMBER_TYPE = "richTextColumns";

const PORTABLE_TEXT_ACCORDION_MEMBER_TYPE = "richTextAccordion";

/**
 * `dynamicPlaceholder` is declared as a mark annotation, so it normally lives
 * in a block's `markDefs` rather than as an array member. It stays on this
 * list because Studio field-path extraction has always accepted it at member
 * level, and a value shaped that way is still rich text rather than a plain
 * array of objects.
 */
const PORTABLE_TEXT_MEMBER_TYPES: readonly string[] = [
  "block",
  "span",
  "dynamicPlaceholder",
  ...PORTABLE_TEXT_MEDIA_MEMBER_TYPES,
  PORTABLE_TEXT_COLUMNS_MEMBER_TYPE,
  PORTABLE_TEXT_ACCORDION_MEMBER_TYPE,
];

function isPortableTextMember(item: unknown): boolean {
  if (typeof item !== "object" || item === null || !("_type" in item)) {
    return false;
  }

  const { _type } = item as { _type: unknown };
  return (
    typeof _type === "string" && PORTABLE_TEXT_MEMBER_TYPES.includes(_type)
  );
}

/**
 * Detects whether an array holds Portable Text rather than arbitrary objects.
 *
 * Matching on member `_type` is what separates rich text from the many other
 * arrays of Sanity objects a document carries (awards, FAQs, team members):
 * those are structurally identical — `_type` plus `_key` — but are not
 * renderable as rich text. Requiring a text block with `children` instead
 * would misread media-only rich text, which has no text block at all.
 */
function isPortableTextArray(value: unknown): boolean {
  return Array.isArray(value) && value.some(isPortableTextMember);
}

export {
  isPortableTextArray,
  PORTABLE_TEXT_ACCORDION_MEMBER_TYPE,
  PORTABLE_TEXT_COLUMNS_MEMBER_TYPE,
  PORTABLE_TEXT_MEDIA_MEMBER_TYPES,
  PORTABLE_TEXT_MEMBER_TYPES,
};
